import React, { useState, useEffect } from "react";
import { SquadflixFriendsApi, UserSummary } from "../../../../../apis/squadflix-friends/SquadflixFriendsApi";
import UserInfo from "../../../../shared/userInfo/user-info";
import { Spinner } from "../../../../shared";
import auth from "../../../../../utils/authentication";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import users from "../../../../users";
import "./MyProfileFriends.scss";

interface MyProfileFriendsProps {
    onRequestsChange?: () => void;
}

const MyProfileFriends: React.FC<MyProfileFriendsProps> = ({ onRequestsChange }) => {
    const darkMode = useSelector(users.selectors.isDarkMode);
    const [activeTab, setActiveTab] = useState<'friends' | 'requests' | 'search'>('friends');
    const [friends, setFriends] = useState<UserSummary[]>([]);
    const [sentRequests, setSentRequests] = useState<UserSummary[]>([]);
    const [receivedRequests, setReceivedRequests] = useState<UserSummary[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<UserSummary[]>([]);

    const [loading, setLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const currentUserId = auth.currentUser?.uid;

    const fetchData = async () => {
        setLoading(true);
        try {
            const [friendsRes, requestsRes] = await Promise.all([
                SquadflixFriendsApi.listFriends(),
                SquadflixFriendsApi.listFriendRequests()
            ]);

            if (friendsRes.success) {
                setFriends(friendsRes.friends);
            }
            if (requestsRes.success) {
                setSentRequests(requestsRes.sent || []);
                setReceivedRequests(requestsRes.received || []);
            }
        } catch (error) {
            console.error("Error fetching friends data:", error);
            toast.error("Error al cargar la información de amigos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const notifyParent = () => {
        if (onRequestsChange) {
            onRequestsChange();
        }
    };

    const handleSendRequest = async (targetUid: string, displayName: string) => {
        try {
            const res = await SquadflixFriendsApi.sendFriendRequest({ targetUid });
            if (res.success) {
                if (res.status === 'accepted') {
                    toast.success(`¡Ahora eres amigo de ${displayName}!`);
                } else {
                    toast.info(`Solicitud de amistad enviada a ${displayName}`);
                }
                fetchData();
                notifyParent();
            }
        } catch (error) {
            console.error("Error sending friend request:", error);
            toast.error("No se pudo enviar la solicitud de amistad");
        }
    };

    const handleCancelRequest = async (targetUid: string) => {
        try {
            const res = await SquadflixFriendsApi.cancelFriendRequest({ targetUid });
            if (res.success) {
                toast.info("Solicitud de amistad cancelada");
                fetchData();
                notifyParent();
            }
        } catch (error) {
            console.error("Error cancelling request:", error);
            toast.error("No se pudo cancelar la solicitud");
        }
    };

    const handleAcceptRequest = async (targetUid: string, displayName: string) => {
        try {
            const res = await SquadflixFriendsApi.acceptFriendRequest({ targetUid });
            if (res.success) {
                toast.success(`¡Solicitud aceptada! Ahora eres amigo de ${displayName}`);
                fetchData();
                notifyParent();
            }
        } catch (error) {
            console.error("Error accepting request:", error);
            toast.error("No se pudo aceptar la solicitud");
        }
    };

    const handleRejectRequest = async (targetUid: string) => {
        try {
            const res = await SquadflixFriendsApi.rejectFriendRequest({ targetUid });
            if (res.success) {
                toast.info("Solicitud de amistad rechazada");
                fetchData();
                notifyParent();
            }
        } catch (error) {
            console.error("Error rejecting request:", error);
            toast.error("No se pudo rechazar la solicitud");
        }
    };

    const handleRemoveFriend = async (targetUid: string, displayName: string) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar a ${displayName} de tus amigos?`)) {
            return;
        }
        try {
            const res = await SquadflixFriendsApi.removeFriend({ targetUid });
            if (res.success) {
                toast.info(`Eliminaste a ${displayName} de tus amigos`);
                fetchData();
                notifyParent();
            }
        } catch (error) {
            console.error("Error removing friend:", error);
            toast.error("No se pudo eliminar al amigo");
        }
    };

    const handleSearchSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const query = searchQuery.trim();
        if (query.length < 2) return;

        setSearchLoading(true);
        setHasSearched(true);
        try {
            const res = await SquadflixFriendsApi.searchUsers({ query });
            if (res.success) {
                setSearchResults(res.results || []);
            }
        } catch (error) {
            console.error("Error searching users:", error);
            toast.error("Error al realizar la búsqueda");
        } finally {
            setSearchLoading(false);
        }
    };

    const getRelationshipStatus = (uid: string): 'friend' | 'sent' | 'received' | 'none' => {
        if (friends.some(f => f.uid === uid)) return 'friend';
        if (sentRequests.some(r => r.uid === uid)) return 'sent';
        if (receivedRequests.some(r => r.uid === uid)) return 'received';
        return 'none';
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner />
            </div>
        );
    }

    return (
        <div className={`my-profile-friends ${!darkMode ? 'my-profile-friends--light' : ''}`}>
            <div className="my-profile-friends__nav">
                <button
                    className={`my-profile-friends__nav-btn ${activeTab === 'friends' ? 'active' : ''}`}
                    onClick={() => setActiveTab('friends')}
                >
                    Mis Amigos ({friends.length})
                </button>
                <button
                    className={`my-profile-friends__nav-btn ${activeTab === 'requests' ? 'active' : ''}`}
                    onClick={() => setActiveTab('requests')}
                >
                    Solicitudes
                    {(receivedRequests.length) > 0 && (
                        <span className="my-profile-friends__badge">{receivedRequests.length}</span>
                    )}
                </button>
                <button
                    className={`my-profile-friends__nav-btn ${activeTab === 'search' ? 'active' : ''}`}
                    onClick={() => setActiveTab('search')}
                >
                    Buscar
                </button>
            </div>

            <div className="my-profile-friends__content">
                {activeTab === 'friends' && (
                    <>
                        {friends.length === 0 ? (
                            <div className="my-profile-friends__empty">
                                <i className="fa-solid fa-user-group"></i>
                                <div className="my-profile-friends__empty-title">Aún no tienes amigos</div>
                                <p>Busca usuarios en la pestaña "Buscar" para agregarlos como amigos.</p>
                            </div>
                        ) : (
                            <div className="my-profile-friends__list">
                                {friends.map(friend => (
                                    <div key={friend.uid} className="my-profile-friends__item">
                                        <UserInfo initialUser={friend} />
                                        <div className="my-profile-friends__actions">
                                            <button
                                                className="my-profile-friends__action-btn my-profile-friends__action-btn--remove"
                                                onClick={() => handleRemoveFriend(friend.uid, friend.displayName)}
                                            >
                                                <i className="fa-solid fa-trash-can"></i> Eliminar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'requests' && (
                    <div>
                        <div className="my-profile-friends__section-title">
                            Recibidas ({receivedRequests.length})
                        </div>
                        {receivedRequests.length === 0 ? (
                            <div className="my-profile-friends__empty" style={{ padding: '1.5rem 1rem' }}>
                                <i className="fa-solid fa-envelope-open" style={{ fontSize: '1.8rem' }}></i>
                                <div className="my-profile-friends__empty-title" style={{ fontSize: '0.95rem' }}>No tienes solicitudes entrantes</div>
                            </div>
                        ) : (
                            <div className="my-profile-friends__list mb-4">
                                {receivedRequests.map(req => (
                                    <div key={req.uid} className="my-profile-friends__item">
                                        <UserInfo initialUser={req} />
                                        <div className="my-profile-friends__actions">
                                            <button
                                                className="my-profile-friends__action-btn my-profile-friends__action-btn--accept"
                                                onClick={() => handleAcceptRequest(req.uid, req.displayName)}
                                            >
                                                <i className="fa-solid fa-check"></i> Aceptar
                                            </button>
                                            <button
                                                className="my-profile-friends__action-btn my-profile-friends__action-btn--reject"
                                                onClick={() => handleRejectRequest(req.uid)}
                                            >
                                                <i className="fa-solid fa-xmark"></i> Rechazar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="my-profile-friends__section-title mt-4">
                            Enviadas ({sentRequests.length})
                        </div>
                        {sentRequests.length === 0 ? (
                            <div className="my-profile-friends__empty" style={{ padding: '1.5rem 1rem' }}>
                                <i className="fa-solid fa-paper-plane" style={{ fontSize: '1.8rem' }}></i>
                                <div className="my-profile-friends__empty-title" style={{ fontSize: '0.95rem' }}>No has enviado solicitudes</div>
                            </div>
                        ) : (
                            <div className="my-profile-friends__list">
                                {sentRequests.map(req => (
                                    <div key={req.uid} className="my-profile-friends__item">
                                        <UserInfo initialUser={req} />
                                        <div className="my-profile-friends__actions">
                                            <button
                                                className="my-profile-friends__action-btn my-profile-friends__action-btn--cancel"
                                                onClick={() => handleCancelRequest(req.uid)}
                                            >
                                                <i className="fa-solid fa-ban"></i> Cancelar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'search' && (
                    <div>
                        <form onSubmit={handleSearchSubmit} className="my-profile-friends__search-container">
                            <input
                                type="text"
                                className="my-profile-friends__search-input"
                                placeholder="Buscar por nombre..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button
                                type="submit"
                                className="btn btn-primary my-profile-friends__search-btn"
                                disabled={searchQuery.trim().length < 2}
                            >
                                <i className="fa-solid fa-magnifying-glass"></i> Buscar
                            </button>
                        </form>

                        {searchLoading ? (
                            <div className="d-flex justify-content-center py-4">
                                <Spinner />
                            </div>
                        ) : (
                            <>
                                {hasSearched && searchResults.length === 0 && (
                                    <div className="my-profile-friends__empty">
                                        <i className="fa-solid fa-user-slash"></i>
                                        <div className="my-profile-friends__empty-title">No se encontraron usuarios</div>
                                        <p>Prueba con otros términos de búsqueda.</p>
                                    </div>
                                )}

                                {searchResults.length > 0 && (
                                    <div className="my-profile-friends__list">
                                        {searchResults.map(user => {
                                            const status = getRelationshipStatus(user.uid);
                                            const isSelf = user.uid === currentUserId;

                                            return (
                                                <div key={user.uid} className="my-profile-friends__item">
                                                    <UserInfo initialUser={user} />
                                                    <div className="my-profile-friends__actions">
                                                        {isSelf ? (
                                                            <span className="badge bg-secondary p-2">Tú</span>
                                                        ) : (
                                                            <>
                                                                {status === 'friend' && (
                                                                    <button
                                                                        className="my-profile-friends__action-btn my-profile-friends__action-btn--remove"
                                                                        onClick={() => handleRemoveFriend(user.uid, user.displayName)}
                                                                    >
                                                                        <i className="fa-solid fa-user-minus"></i> Eliminar
                                                                    </button>
                                                                )}
                                                                {status === 'sent' && (
                                                                    <button
                                                                        className="my-profile-friends__action-btn my-profile-friends__action-btn--cancel"
                                                                        onClick={() => handleCancelRequest(user.uid)}
                                                                    >
                                                                        <i className="fa-solid fa-ban"></i> Cancelar
                                                                    </button>
                                                                )}
                                                                {status === 'received' && (
                                                                    <>
                                                                        <button
                                                                            className="my-profile-friends__action-btn my-profile-friends__action-btn--accept"
                                                                            onClick={() => handleAcceptRequest(user.uid, user.displayName)}
                                                                        >
                                                                            <i className="fa-solid fa-check"></i> Aceptar
                                                                        </button>
                                                                        <button
                                                                            className="my-profile-friends__action-btn my-profile-friends__action-btn--reject"
                                                                            onClick={() => handleRejectRequest(user.uid)}
                                                                        >
                                                                            <i className="fa-solid fa-xmark"></i> Rechazar
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {status === 'none' && (
                                                                    <button
                                                                        className="my-profile-friends__action-btn my-profile-friends__action-btn--add"
                                                                        onClick={() => handleSendRequest(user.uid, user.displayName)}
                                                                    >
                                                                        <i className="fa-solid fa-user-plus"></i> Agregar
                                                                    </button>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyProfileFriends;
